import { CELL, GROUND } from '../core/config';
import { key, MATERIALS, type Building, type City, type Place, type RoadEdge } from './city';
import { roadLaneOffset, roadLength, roadPoint, roadSegment } from './roads';
import type { Vec } from '../destruction/structure';

export interface LoadingBay {
  id: string;
  name: string;
  edge: number;
  from: number;
  t: number;
  lane: Vec;
  park: Vec;
  direction: Vec;
  worker: Place;
  surface: number;
  destinations: string[];
}
export interface LogisticsLayout {
  hubs: LoadingBay[];
  stops: LoadingBay[];
  servicePoints: Record<string, string>;
}

/** Legal off-lane bays. Their pavement, sign and represented interaction targets
 * are ordinary damageable world cells; nothing is respawned to complete a job. */
export function prepareLogistics(city: City): City {
  if (city.logistics || !city.regional || !city.roadGraph || !city.places) return city;
  const all = [
    ...city.buildings,
    ...(city.props ?? []),
    ...(city.infrastructure ?? []),
    ...(city.bridges ?? []),
  ];
  let serial = Math.max(...all.map((b) => b.id)) + 1;
  const bays: LoadingBay[] = [];
  const servicePoints: Record<string, string> = {};
  const servicePoint = (target: Place): boolean => {
    if (servicePoints[target.id]) return true;
    const building = city.buildings.find((b) => b.id === target.buildingId);
    if (!building) return false;
    // Customers use the front of a shelf; stock workers use a separate, real
    // supported space beside/behind it. They do not evict a customer remotely.
    for (const [dx, dz] of [
      [0, 2.5],
      [1.25, 1.25],
      [-1.25, 1.25],
    ]) {
      const x = target.x + dx,
        z = target.z + dz,
        gx = Math.floor((x - building.x) / CELL),
        gz = Math.floor((z - building.z) / CELL);
      if (gx < 1 || gz < 1 || gx >= building.w - 1 || gz >= building.d - 1) continue;
      const floor = building.cells.find((c) => c.x === gx && c.z === gz && c.y === 0);
      if (!floor || building.cells.some((c) => c.x === gx && c.z === gz && c.y > 0 && c.y < 3)) continue;
      if (
        city.props?.some(
          (p) =>
            p.kind === 'furniture' &&
            Math.abs((p.baseY ?? GROUND) - target.y) < 0.4 &&
            x > p.x - 0.28 &&
            x < p.x + p.w * CELL + 0.28 &&
            z > p.z - 0.28 &&
            z < p.z + p.d * CELL + 0.28,
        )
      )
        continue;
      if (city.places!.some((p) => Math.hypot(p.x - x, p.y - target.y, p.z - z) < 0.7)) continue;
      const place: Place = {
        ...target,
        id: `stock-access:${target.id}`,
        kind: 'loading',
        name: `${target.name} · stock access`,
        x,
        z,
        supportCell: floor.id,
        capacity: 1,
      };
      city.places!.push(place);
      servicePoints[target.id] = place.id;
      return true;
    }
    return false;
  };
  const edges = city.roadGraph.edges.filter(
    (e) => !e.curved && !e.bridgeId && e.width >= 7 && e.kind !== 'airport service' && roadLength(e) > 55,
  );
  const clear = (p: Vec, dx: number, dz: number) => {
    const hx = Math.abs(dx) * 7 + Math.abs(dz) * 2.2,
      hz = Math.abs(dz) * 7 + Math.abs(dx) * 2.2;
    return (
      ![...city.buildings, ...(city.props ?? [])].some(
        (b) =>
          b.x < p.x + hx + 0.5 &&
          b.x + b.w * CELL > p.x - hx - 0.5 &&
          b.z < p.z + hz + 0.5 &&
          b.z + b.d * CELL > p.z - hz - 0.5,
      ) &&
      !bays.some((b) => Math.hypot(b.park.x - p.x, b.park.z - p.z) < 18) &&
      !city.streetTransit?.stops.some((s) => Math.hypot(s.point.x - p.x, s.point.z - p.z) < 19)
    );
  };
  const make = (target: Place, label: string): LoadingBay | undefined => {
    const options: { edge: RoadEdge; t: number; p: Vec; direction: Vec; distance: number }[] = [];
    for (const edge of edges) {
      const a = roadPoint(edge, 0),
        b = roadPoint(edge, 1),
        len = roadLength(edge),
        direction = { x: (b.x - a.x) / len, y: 0, z: (b.z - a.z) / len };
      for (const t of [0.25, 0.4, 0.6, 0.75]) {
        const p = roadPoint(edge, t, edge.width / 2 + 4.6),
          distance = Math.hypot(target.x - p.x, target.z - p.z);
        if (distance < 145 && clear(p, direction.x, direction.z))
          options.push({ edge, t, p, direction, distance });
      }
    }
    options.sort((a, b) => a.distance - b.distance);
    const selected = options[0];
    if (!selected) return;
    const { edge, t, p, direction } = selected,
      side = { x: -direction.z, y: 0, z: direction.x },
      id = `loading-bay:${bays.length}`,
      surfaceId = serial++;
    // Flush apron and driveway keep the pedestrian path continuous. Parking is
    // farther out than the pavement and clear of the active traffic envelope.
    p.y = GROUND + 0.12;
    const floor = roadSegment(
      surfaceId,
      { ...edge, kind: 'loading bay', width: 5 },
      { x: p.x - direction.x * 7, y: p.y, z: p.z - direction.z * 7 },
      { x: p.x + direction.x * 7, y: p.y, z: p.z + direction.z * 7 },
      2.1,
    );
    floor.name = `${label} · loading apron`;
    floor.cells.forEach(
      (c) =>
        (c.color =
          c.x === 0 || c.x === floor.w - 1 || c.z === 0 || c.z === floor.d - 1 ? 0xc8b992 : 0x8a9388),
    );
    (city.infrastructure ??= []).push(floor);
    const pole = { x: p.x + direction.x * 6.2 + side.x * 1.7, z: p.z + direction.z * 6.2 + side.z * 1.7 },
      signId = serial++;
    const sign: Building = {
      id: signId,
      name: `${label} · loading sign`,
      kind: 'road',
      category: 'prop',
      x: pole.x,
      z: pole.z,
      baseY: p.y,
      w: 1,
      d: 1,
      h: 2,
      color: 0x748679,
      accent: 0xd4c495,
      cells: [],
    };
    for (let y = 0; y < 2; y++)
      sign.cells.push({
        id: signId * 1e6 + key(0, y, 0),
        x: 0,
        y,
        z: 0,
        hp: MATERIALS.metal.strength,
        material: 'metal',
        role: 'column',
        root: y === 0,
        color: y ? 0xd4c495 : 0x708276,
        bounds: { x: 0, y: 0, z: 0, w: (y ? 0.65 : 0.12) / CELL, h: (y ? 0.4 : CELL) / CELL, d: 0.12 / CELL },
      });
    (city.props ??= []).push(sign);
    const worker: Place = {
      id: `${id}:door`,
      name: `${label} · van loading door`,
      kind: 'loading',
      floor: 0,
      capacity: 1,
      duration: 2,
      x: p.x - side.x * 1.85,
      y: p.y,
      z: p.z - side.z * 1.85,
      supportCell: sign.cells[0].id,
    };
    city.places!.push(worker);
    const bay: LoadingBay = {
      id,
      name: label,
      edge: edge.id,
      from: edge.from,
      t,
      lane: roadPoint(edge, t, roadLaneOffset(edge.width)),
      park: p,
      direction,
      worker,
      surface: surfaceId,
      destinations: [target.id],
    };
    bays.push(bay);
    return bay;
  };
  const sources = city.places
    .filter((p) => p.kind === 'materials' && p.floor === 0 && servicePoint(p))
    .sort((a, b) => Math.hypot(a.x + 60, a.z + 30) - Math.hypot(b.x + 60, b.z + 30));
  const hubs: LoadingBay[] = [];
  for (const source of sources) {
    if (
      hubs.some(
        (h) =>
          h.destinations.includes(source.id) || Math.hypot(h.park.x - source.x, h.park.z - source.z) < 40,
      )
    )
      continue;
    const hub = make(source, `Canal logistics ${hubs.length + 1}`);
    if (hub) {
      hub.destinations = sources.filter((p) => p.buildingId === source.buildingId).map((p) => p.id);
      hubs.push(hub);
    }
    if (hubs.length === 3) break;
  }
  const stops: LoadingBay[] = [];
  for (const shop of city.places
    .filter((p) => p.kind === 'display' && p.floor === 0 && servicePoint(p))
    .sort((a, b) => Math.hypot(a.x + 40, a.z + 30) - Math.hypot(b.x + 40, b.z + 30))) {
    if (
      stops.some((s) =>
        s.destinations.some((id) => city.places!.find((p) => p.id === id)?.buildingId === shop.buildingId),
      )
    )
      continue;
    const stop = make(shop, `Market delivery ${stops.length + 1}`);
    if (stop) {
      stop.destinations = city.places
        .filter(
          (p) =>
            p.kind === 'display' && p.buildingId === shop.buildingId && p.floor === 0 && servicePoints[p.id],
        )
        .map((p) => p.id);
      stops.push(stop);
    }
    if (stops.length === 8) break;
  }
  city.logistics = { hubs, stops, servicePoints };
  return city;
}
